import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sparkles, Send, FileText, Wallet, MessageCircle, TrendingUp, Bot, User } from 'lucide-react';
import { useAssistant } from '@/context/AssistantContext';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

const REMARK_PLUGINS = [remarkGfm];

const QUICK_PROMPTS = [
  { icon: Wallet, label: 'Cek piutang', prompt: 'Berapa total piutang saya dan siapa saja pelanggan yang belum lunas?' },
  { icon: TrendingUp, label: 'Omzet bulan ini', prompt: 'Berapa omzet bulan ini dan bagaimana perbandingannya dengan proyek yang masih berjalan?' },
  { icon: MessageCircle, label: 'Proyek nyangkut', prompt: 'Proyek mana saja yang masih nyangkut di produksi atau pemasangan?' },
];

let _msgSeq = 0;
const mid = () => `msg-${++_msgSeq}`;

export function AIAssistant() {
  const { open, setOpen, seedPrompt, setSeedPrompt } = useAssistant();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { id: mid(), role: 'assistant', content: 'Halo! Saya **Asisten AluminaOne**. Tanya apa saja soal penawaran, harga, piutang, atau follow-up pelanggan. Saya jawab berdasarkan data toko Anda.' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (seedPrompt) {
      setInput(seedPrompt);
      setSeedPrompt('');
    }
  }, [seedPrompt, setSeedPrompt]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.filter((m) => m.role === 'user' || m.role === 'assistant').slice(-6);
    setMessages((m) => [...m, { id: mid(), role: 'user', content: msg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api.post('/ai/chat', { message: msg, history });
      setMessages((m) => [...m, { id: mid(), role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Maaf, terjadi kesalahan. Coba lagi ya.';
      setMessages((m) => [...m, { id: mid(), role: 'assistant', content: detail }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="ai-assistant-drawer" side="right" className="flex w-full flex-col p-0 sm:max-w-md">
        <SheetHeader className="border-b px-4 py-3">
          <SheetTitle className="flex items-center gap-2 font-display">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Sparkles className="h-4 w-4" />
            </span>
            Asisten AluminaOne
          </SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="chat" className="flex flex-1 flex-col overflow-hidden">
          <TabsList className="mx-4 mt-3 grid w-auto grid-cols-2">
            <TabsTrigger value="chat">Chat</TabsTrigger>
            <TabsTrigger value="aksi">Aksi Cepat</TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="flex flex-1 flex-col overflow-hidden m-0">
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.map((m) => (
                <div key={m.id} className={cn('flex gap-2', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                  {m.role === 'assistant' && (
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Bot className="h-4 w-4" /></span>
                  )}
                  <div className={cn('markdown-body max-w-[80%] rounded-2xl px-3 py-2 text-sm',
                    m.role === 'user' ? 'rounded-br-md bg-primary text-primary-foreground' : 'rounded-bl-md border bg-muted text-foreground')}>
                    {m.role === 'assistant'
                      ? <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{m.content}</ReactMarkdown>
                      : <span>{m.content}</span>}
                  </div>
                  {m.role === 'user' && (
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"><User className="h-4 w-4" /></span>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-2 justify-start">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Bot className="h-4 w-4" /></span>
                  <div className="rounded-2xl rounded-bl-md border bg-muted px-3 py-2.5">
                    <span className="flex gap-1">
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '0ms' }} />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '150ms' }} />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/60" style={{ animationDelay: '300ms' }} />
                    </span>
                  </div>
                </div>
              )}
            </div>

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-4 pb-2">
                {QUICK_PROMPTS.map((q) => (
                  <button key={q.label} data-testid="ai-assistant-quick-action" onClick={() => send(q.prompt)}
                    className="flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted">
                    <q.icon className="h-3.5 w-3.5 text-primary" /> {q.label}
                  </button>
                ))}
              </div>
            )}

            <div className="border-t p-3">
              <div className="flex items-end gap-2">
                <Textarea
                  data-testid="ai-assistant-message-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Tulis pertanyaan..."
                  className="min-h-[44px] max-h-28 resize-none rounded-xl"
                  rows={1}
                />
                <Button data-testid="ai-assistant-send-button" onClick={() => send()} disabled={loading || !input.trim()} size="icon" className="h-11 w-11 shrink-0 rounded-xl">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="aksi" className="flex-1 overflow-y-auto p-4 m-0 space-y-3">
            <p className="text-sm text-muted-foreground">Pintasan cepat dengan bantuan AI:</p>
            <button onClick={() => { setOpen(false); navigate('/penawaran/baru?tab=ai'); }}
              className="flex w-full items-center gap-3 rounded-xl border bg-card p-3 text-left hover:bg-muted">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileText className="h-5 w-5" /></span>
              <span><span className="block text-sm font-semibold">Buat RAB dengan AI</span><span className="block text-xs text-muted-foreground">Deskripsikan pekerjaan, AI buatkan penawaran</span></span>
            </button>
            {QUICK_PROMPTS.map((q) => (
              <button key={q.label} onClick={() => { const tab = document.querySelector('[value="chat"]'); send(q.prompt); }}
                className="flex w-full items-center gap-3 rounded-xl border bg-card p-3 text-left hover:bg-muted">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent"><q.icon className="h-5 w-5" /></span>
                <span className="text-sm font-semibold">{q.label}</span>
              </button>
            ))}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
